import dayjs from "dayjs";
import { getDivisionChoices } from "../cclib/CCUtility.ts";
import {
  ConferenceStructure,
  laheyCCManage,
  laheyCCSetup,
  overrideStructure
} from "./lah_helper.js";

let sortBy = "mentor";
const dataPrefix = "lah_chief_";

// This is like the 'format input' object part that defines the layout of tab
// name: label next to the text box
// type: integer, float, string, header, paragraph, select (option), previous (last year's value)

function buildStructure(choices = []) {
  const structure = new ConferenceStructure();
  structure.insertAfter("department_name", {
    name: "How many direct reports do you have?",
    type: "string",
    key: "direct_reports_count"
  });
  structure.replace("subspecialty", {
    name: "Primary Rad Subspecialty Section",
    type: "select",
    key: "rad_subspecialty",
    options: choices
  });
  structure.replace("job_dissatisfiers", {
    name: "Job Dissatisfiers (What do you see as the biggest challenge(s) you face when it comes to your work?)",
    type: "paragraph",
    key: "job_dissatisfiers"
  });
  structure.removeByName("What are your career goals?");
  structure.insertBefore("immediate_goals", {
    name: "Ideally, how would you like to see your career progress over the next 1-5 years?",
    type: "header"
  });
  structure.removeByName("Other Goals (specify)");
  structure.remove("other_goals_appraiser_comments");
  structure.replace("colleague_comments", {
    name: "Colleague Comments: e.g. What talents, interests or skills do you have that we haven’t made the most of? When you take a step back and consider the past 12 months or so, how would you summarize your professional or personal journey? What would you like to tell us that we need to consider to help you succeed?",
    type: "paragraph",
    key: "colleague_comments"
  });
  let stack = [
    { name: "Productivity", type: "header" },
    { name: "RVU for this year", type: "string", key: "rvu_this_year" },
    { name: "Clinical FTE", type: "string", key: "clinical_fte" },
    {
      name: "Next year, I plan for my productivity to",
      type: "select",
      key: "rvu_next_year",
      options: ["Stay similar to this year", "Increase", "Other"]
    },
    {
      name: "If other, please specify",
      type: "paragraph",
      key: "other_specify",
      optional: true
    },
    {
      name: "If you plan to increase productivity, please list approximately by how many RVU/year",
      type: "string",
      key: "rvu_increase_amount",
      optional: true
    },
    {
      name: "What are your intended tactics to achieve this goal?",
      type: "paragraph",
      key: "rvu_increase_tactics"
    },
    {
      name: "Appraiser Comments",
      type: "paragraph",
      key: "rvu_appraiser_comments",
      isMentorField: true,
      optional: true
    },
    { name: "Succession Planning & Professional Development", type: "header" },
    {
      name: "Please Share your thoughts on succession planning in your section or area of responsibility. (Specify WHO, WHEN and WHAT type of succession/professional development you are discussing)",
      type: "paragraph",
      key: "succession_planning"
    },
    {
      name: "Appraiser Comments",
      type: "paragraph",
      key: "succession_appraiser_comments",
      isMentorField: true,
      optional: true
    }
  ].reverse();
  let i = structure.insertAt(
    structure.getIndex("adv_goals_appraiser_comments") + 1,
    stack.pop()
  );
  while (stack.length) {
    i = structure.insertAt(i, stack.pop());
  }
  return structure;
}

export default {
  title: "Lahey Radiology (Section Chief, Vice Chair)",
  tabTitle: "Annual Performance Evaluation",
  cvSetup: function ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend
  }) {
    const { hierarchy, hierarchyNames } = contextConstants;
    let choices = getDivisionChoices(conference, hierarchy, hierarchyNames);

    overrideStructure(buildStructure(choices));
    laheyCCSetup(
      contextMethods,
      contextConstants,
      contextState,
      conference,
      backend,
      dataPrefix,
      "lahey_radiology_chief_report_v1_0.docx",
      "Lahey Radiology Chiefs Review Form",
      "301155080",
      function generateOutputData(data, elementArray, props) {
        const {
          departmentName,
          departmentId,
          appraiserName,
          appraisalPeriod,
          yearData,
          lastYearData,
          startDate,
          newDef
        } = props;
        data["department_name"] = departmentName;
        data["department_id"] = departmentId;
        data["appraiser_name"] = appraiserName;
        data["appraisal_period"] = appraisalPeriod;
        data["date_appraisal_completed"] = yearData?.data?.chairSign
          ? dayjs(yearData?.data?.chairSign.time).format("M/D/YYYY")
          : "";
        data["appraiser_signature"] =
          yearData?.data?.chairSign?.signature || "";
        data["appraiser_signed_at"] = dayjs(
          yearData?.data?.chairSign?.time || ""
        ).format("M/D/YYYY");
        data["colleague_signature"] =
          yearData?.data?.menteeSign?.signature || "";
        data["colleague_signed_at"] = dayjs(
          yearData?.data?.menteeSign?.time || ""
        ).format("M/D/YYYY");
        data["current_year"] = dayjs(startDate).format("YYYY");
        data["prior_year"] = dayjs(startDate)
          .subtract(1, "year")
          .format("YYYY");
        elementArray.map(function (item) {
          if (item.key != null) {
            if (item.type == "check")
              data[item.key] = !!yearData.data[dataPrefix + item.key];
            else if (item.type == "extraction")
              data[item.key] = item.val(newDef).text;
            else if (item.type == "previous")
              data[item.key] =
                lastYearData.data[dataPrefix + item.key.replace("_prev", "")] ||
                "";
            else data[item.key] = yearData.data[dataPrefix + item.key] || "";
          } else if (item.type == "table" || item.type == "inline-table") {
            generateOutputData(data, item.sections, props);
          }
        });
        if (yearData?.data?.[dataPrefix + "lahey_start_date"] != undefined) {
          data["years_at_lahey"] = dayjs()
            .diff(
              dayjs(yearData.data[dataPrefix + "lahey_start_date"]),
              "year",
              true
            )
            .toFixed(1);
        }
        if (yearData?.data?.[dataPrefix + "job_start_date"] != undefined) {
          data["years_in_job"] = dayjs()
            .diff(
              dayjs(yearData.data[dataPrefix + "job_start_date"]),
              "year",
              true
            )
            .toFixed(2);
        }
        if (
          yearData?.data?.[dataPrefix + "secondary_job_start_date"] != undefined
        ) {
          data["years_in_secondary_job"] = dayjs()
            .diff(
              dayjs(yearData.data[dataPrefix + "secondary_job_start_date"]),
              "year",
              true
            )
            .toFixed(2);
        }
      }
    );
  },
  // This is for setting up the Manage Departments page
  manageSetup: function (
    settingsEl,
    { conference, updateConfDate, saveConference, hierarchyNames, backend },
    user
  ) {
    laheyCCManage(
      settingsEl,
      conference,
      updateConfDate,
      saveConference,
      hierarchyNames,
      backend,
      user
    );
  }
};
